import { useCallback, useEffect, useRef, useState } from "react";
import type { TargetHistoryEntry, TargetItem } from "@jackedlogs/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";

// THE targets read + optimistic-locked write, in ONE place (ME-1).
//
// Two surfaces edit weekly targets: the coach, against a program
// (`/me/programs/:id/targets`, CO-7), and the athlete, against their own doc
// (`/me/targets`, ME-0). The endpoints and the document differ; the machinery —
// read the ETag, hold it in a ref, echo it as If-Match, re-read it off the
// PATCH response, reload history on save, map the wire to a failure reason — is
// identical. So it lives here once, parametrized by path + document type.
// <TargetsEditor> is its only caller and picks the paths from its `source`.
//
// THE LOCK. The PATCH requires `If-Match` (428 without it) and its validator is
// an updatedAt-derived ETag the client cannot compute (sha256 via node:crypto,
// absent in RN). So the ETag is READ off the GET and handed straight back. It is
// also re-read off the PATCH response: without that, a SECOND save in one
// sitting would carry the stale validator and 412 against its own first write.
//
// ⛔ That ETag is the WRITE validator, never a body hash: do not wire it to
// If-None-Match/304 on the GET. The program body carries catalog-resolved
// `warnings` that change with nobody writing.
//
// 🔒 Online-only and fail-loud, deliberately. Neither edit path has an outbox
// (the outbox covers the hot path — workouts/sets/HC/measurements — not this).
// If either ever gains an offline queue, this lock must be re-thought, not
// inherited.

/** Why a save failed, mapped from the wire so the UI never reads raw codes. */
export type TargetsSaveFailure =
  /** 412 — someone wrote between our read and our write. NOT a merge to solve:
   *  reload, read the history to see who changed what, re-apply. */
  | "stale"
  /** 403 no_write_grant — the athlete has not granted write:targets. This is the
   *  consent gate working, not a bug. Unreachable on the self path. */
  | "no_grant"
  /** 403 not_coach — no active edge at all. Unreachable on the self path. */
  | "not_coach"
  /** 409 — a program assigned to 2+ athletes has two authorities; refused
   *  explicitly rather than half-handled. Program path only: the athlete doc is
   *  a singleton with exactly one owner, which is the point of ME-0. */
  | "multi_assignee"
  /** 428 — we sent no If-Match. Only reachable as OUR bug (no ETag in hand). */
  | "missing_validator"
  | "not_found"
  | "offline_or_unknown";

export type TargetsSaveResult<T> =
  // The updated document travels back so the caller can refresh the surfaces
  // that score against these targets without a re-fetch.
  | { ok: true; doc: T }
  | { ok: false; reason: TargetsSaveFailure };

type HistoryResponse = { items?: TargetHistoryEntry[]; truncated?: boolean };

// The 403s are two different human situations (ask for the grant vs you aren't
// their coach), so the server distinguishes them and so must we.
function targetsFailureFor(err: unknown): TargetsSaveFailure {
  const status = (err as { status?: unknown }).status;
  const code = (err as { body?: { error?: unknown } }).body?.error;
  if (status === 412) return "stale";
  if (status === 428) return "missing_validator";
  if (status === 409) return "multi_assignee";
  if (status === 404) return "not_found";
  if (status === 403) {
    return code === "no_write_grant" ? "no_grant" : "not_coach";
  }
  return "offline_or_unknown";
}

export type TargetsDocPaths = {
  /** GET the document + its ETag. */
  doc: string;
  /** GET the server-authored audit trail. */
  history: string;
  /** PATCH the items, carrying If-Match. */
  patch: string;
};

export function useTargetsDoc<T>({
  apiFetch,
  paths,
  itemsOf,
}: {
  /** The host's BASE apiFetch — NOT the athlete-scoped read-only wrapper. */
  apiFetch: ProgramApiFetch;
  /** null = nothing to read yet (e.g. no program id in hand). */
  paths: TargetsDocPaths | null;
  /** Where the target items live inside the document. */
  itemsOf: (doc: T) => TargetItem[];
}) {
  const [doc, setDoc] = useState<T | null>(null);
  const [history, setHistory] = useState<TargetHistoryEntry[] | null>(null);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  // The validator lives in a ref, not state: it must be readable by save()
  // without re-running the effect that produced it, and a stale closure over it
  // is exactly the 412 this whole feature exists to prevent.
  const etagRef = useRef<string | null>(null);

  const docPath = paths?.doc ?? null;
  const historyPath = paths?.history ?? null;
  const patchPath = paths?.patch ?? null;

  const load = useCallback(async () => {
    if (!docPath || !historyPath) return;
    setLoadFailed(false);
    try {
      const [d, h] = await Promise.all([
        apiFetch<T>(docPath, {
          onEtag: (etag) => {
            etagRef.current = etag;
          },
        }),
        apiFetch<HistoryResponse>(historyPath),
      ]);
      setDoc(d);
      setHistory(h.items ?? []);
      // The route caps at 1000 entries. Surface it — a truncated audit trail
      // rendered as complete is a lie about who changed what.
      setHistoryTruncated(h.truncated === true);
    } catch {
      setLoadFailed(true);
    }
  }, [apiFetch, docPath, historyPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (items: TargetItem[]): Promise<TargetsSaveResult<T>> => {
      if (!patchPath) return { ok: false, reason: "not_found" };
      // No validator in hand ⇒ the server would 428. Say so here rather than
      // spending a round-trip to be told.
      if (!etagRef.current) return { ok: false, reason: "missing_validator" };

      setSaving(true);
      try {
        // ⛔ Deliberately NOT on the durableWrite seam (DX-2/E). This PATCH
        // is an optimistic lock: `If-Match` is REQUIRED server-side (428
        // without it — meTargets.ts:131) and the response ETag is the next
        // save's validator. The queue's payload is {method, path, body}, so
        // a replay would arrive with no validator at all and hard-fail. A
        // write that is only valid against the version it read cannot be
        // made durable by replaying it later; it stays online-only, like
        // `DELETE /api/me`.
        const updated = await apiFetch<T>(patchPath, {
          method: "PATCH",
          // min(1) items or null — the schema rejects an empty array, and the
          // key is required (a bare {} is a 400, not a no-op). See
          // PatchAthleteTargets in packages/shared/src/schemas/program.ts.
          body: { targets: items.length ? { scope: "weekly", items } : null },
          headers: { "If-Match": etagRef.current },
          // The next save's validator. Losing this is a self-inflicted 412.
          onEtag: (etag) => {
            etagRef.current = etag;
          },
        });
        setDoc(updated);
        // The server appended the history entry; re-read so the author sees
        // their own change land in the audit trail.
        void load();
        return { ok: true, doc: updated };
      } catch (err) {
        const reason = targetsFailureFor(err);
        // Stale means our whole picture is old, not just the validator. Reload
        // doc + history so "who changed what" is answerable immediately.
        if (reason === "stale") void load();
        return { ok: false, reason };
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, patchPath, load],
  );

  return {
    doc,
    // Called straight through, not memoized: it is a field read on a document we
    // already hold, and it feeds no dependency array.
    targets: doc ? itemsOf(doc) : [],
    history,
    historyTruncated,
    loadFailed,
    saving,
    save,
    reload: load,
  };
}
